import React from "react";

/**
 * Everything the product says when it is not showing data: the page while it
 * loads, an alert that stays put, a state with nothing in it, and the small
 * marks (chips, tiles, avatars) that label what IS there.
 *
 * Presentation only -- every one of these reads props and renders. The look
 * lives in assets/css/_ui.scss.
 */

/**
 * The page while its first request is out: a thin bar across the top and the
 * content behind it dimmed, so the shape of the page is there from the first
 * frame and nothing jumps when the answer lands.
 *
 *   <PageLoading busy={this.state.loading}>
 *     ...the page, exactly as it renders when loaded...
 *   </PageLoading>
 */
export const PageLoading = ({ busy, children }) => (
  <div className="ui-loading-wrap">
    {busy ? (
      <div className="ui-progress" role="progressbar" aria-label="جاري التحميل">
        <span></span>
      </div>
    ) : null}
    <div className={busy ? "ui-dim" : undefined}>{children}</div>
  </div>
);

/** A line that stays on the page, for what a toast would lose. */
export const Alert = ({ kind = "info", icon, title, children }) => {
  const glyph =
    icon ||
    { ok: "pi pi-check-circle", warn: "pi pi-exclamation-triangle", bad: "pi pi-times-circle", info: "pi pi-info-circle" }[
      kind
    ];

  return (
    <div className={`ui-alert is-${kind}`}>
      <i className={glyph}></i>
      <span>
        {title ? <b>{title} </b> : null}
        {children}
      </span>
    </div>
  );
};

/**
 * Nothing here yet, nothing matched, or nothing you may open. One shape for
 * all three, so an empty table and a locked page do not look like two
 * different kinds of dead end.
 *
 *   <EmptyState title="لا يوجد دروس" hint="ابدأ بإضافة أول حصة"
 *               action={{ label: "إضافة حصة", onClick: openAdd }} />
 *   <EmptyState denied title="الصفحة دي مش متاحة لحسابك" ... />
 */
export const EmptyState = ({
  title,
  hint,
  icon = "pi pi-inbox",
  action,
  secondary,
  denied = false,
}) => (
  <div className={denied ? "ui-state is-denied" : "ui-state"}>
    <span className="ui-state-ico" aria-hidden="true">
      <i className={denied ? "pi pi-lock" : icon}></i>
    </span>

    <b>{title}</b>
    {hint ? <span>{hint}</span> : null}

    {action || secondary ? (
      <div className="ui-state-acts">
        {action ? <Act className="ui-btn is-gold" {...action} /> : null}
        {secondary ? <Act className="ui-btn is-secondary" {...secondary} /> : null}
      </div>
    ) : null}
  </div>
);

// an action is { label, icon, url } to go somewhere, or { label, icon, onClick }
// to do something where you stand -- whichever it is decides the element
const Act = ({ className, label, icon, url, onClick }) =>
  onClick ? (
    <button type="button" className={className} onClick={onClick}>
      {icon ? <i className={icon}></i> : null}
      <span>{label}</span>
    </button>
  ) : (
    <a className={className} href={url}>
      {icon ? <i className={icon}></i> : null}
      <span>{label}</span>
    </a>
  );

/**
 * The wait, drawn where the rows will be.
 *
 * PrimeReact's own `loading` floats a spinner over the table, which above an
 * empty one is a spinner in mid-air. This goes into `emptyMessage` instead,
 * so the answer appears exactly where the waiting was and the table does not
 * change shape when it ends.
 *
 *   emptyMessage={loading ? <TableWait /> : <EmptyState ... />}
 */
export const TableWait = ({
  title = "جارٍ التحميل…",
  hint = "لحظة واحدة، بنجيب البيانات",
}) => (
  <div className="ui-state is-wait" role="status">
    <span className="ui-state-spin" aria-hidden="true"></span>
    <b>{title}</b>
    <span>{hint}</span>
  </div>
);

/** A state word with its own colour: ok | wait | off | bad. */
export const Chip = ({ kind = "off", children }) => (
  <span className={`ui-chip is-${kind}`}>
    <span className="dot" aria-hidden="true"></span>
    {children}
  </span>
);

/**
 * One number worth reading at a glance.
 *
 * `icon` puts a glyph on a tinted plate beside the figure, and `tone` picks
 * which colour that plate is -- teal | gold | green | red -- so a row of
 * tiles can be told apart before a single word of it is read. `brand` paints
 * the whole tile in the gradient instead, for the one figure that matters
 * most in the row.
 *
 *   <StatTile icon="pi pi-users" tone="teal" label="عدد الطلاب" value={42} />
 *   <StatTile brand icon="pi pi-wallet" label="إجمالي المبالغ" value="٨٠٠ ج.م" />
 */
export const StatTile = ({ label, value, delta, icon, tone = "teal", brand = false }) => (
  <div className={`ui-tile is-${tone}${brand ? " is-brand" : ""}`}>
    {icon ? (
      <span className="ic" aria-hidden="true">
        <i className={icon}></i>
      </span>
    ) : null}

    <span className="tx">
      <span className="k">{label}</span>
      <span className="v">{value}</span>
      {delta ? <span className="d">{delta}</span> : null}
    </span>
  </div>
);

/** Whoever a row is about: initials, name, and the quiet line under it. */
export const UserCell = ({ name, sub }) => (
  <span className="ui-user">
    <span className="pic" aria-hidden="true">
      {String(name || "")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w.charAt(0))
        .join("")}
    </span>
    <span>
      <b>{name}</b>
      {sub ? <small>{sub}</small> : null}
    </span>
  </span>
);

/** A short explanation that appears on hover, for an icon-only control. */
export const Tip = ({ text, children }) => (
  <span className="ui-tip">
    {children}
    <span className="tip">{text}</span>
  </span>
);
